// shared/hooks/usePagination.js

import { useState, useMemo } from "react";

/**
 * usePagination – generic client-side pagination hook
 *
 * @param {Array}  data       – the full array to paginate
 * @param {number} pageSize   – rows per page (default 10)
 * @returns {{
 *   currentPage  : number,
 *   totalPages   : number,
 *   pageData     : Array,
 *   goToPage     : (n: number) => void,
 *   nextPage     : () => void,
 *   prevPage     : () => void,
 *   setPageSize  : (n: number) => void,
 *   pageSize     : number,
 *   totalItems   : number,
 *   startIndex   : number,
 *   endIndex     : number,
 * }}
 */
const usePagination = (data = [], initialPageSize = 10) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reset to page 1 whenever data length or pageSize changes
  useMemo(() => {
    setCurrentPage(1);
  }, [totalItems, pageSize]);

  const safePage = Math.min(currentPage, totalPages);

  const startIndex = (safePage - 1) * pageSize;           // 0-based, inclusive
  const endIndex   = Math.min(startIndex + pageSize, totalItems); // exclusive

  const pageData = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex]
  );

  const goToPage = (n) => {
    const page = Math.max(1, Math.min(n, totalPages));
    setCurrentPage(page);
  };

  const setPageSize = (n) => {
    setPageSizeState(Number(n));
    setCurrentPage(1);
  };

  return {
    currentPage: safePage,
    totalPages,
    pageData,
    goToPage,
    nextPage : () => goToPage(safePage + 1),
    prevPage : () => goToPage(safePage - 1),
    setPageSize,
    pageSize,
    totalItems,
    startIndex: startIndex + 1,   // 1-based for display
    endIndex,
  };
};

export default usePagination;
